import { AuthContext } from '@@/contexts/AuthContext';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ResultValue } from '@@/types/ApiResult';
import { useQuery } from '@tanstack/react-query';
import { CommonApiClient } from '@@/api/api-client';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import { getRolesPermissionsResponse } from '@@/api/api-common';
import { SelectProps } from '@cloudscape-design/components';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { StorageHelper } from '@@/lib/StorageHelper';
import { InstanceData } from '@@/types/InstanceData';

export const GET_ROLE_PERMISSIONS_QUERY_KEY = 'getRolePermissions';

const AuthProvider = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const getRolePermissions = useCallback(async () => {
    try {
      const client = new CommonApiClient();
      const result = await client.common.getRolePermissions();
      if (result?.ok) {
        return result.data;
      }
      if (ResultValue.error(result)) {
        if (Array.isArray(result.message)) {
          throw new Error(result.message.join(', '));
        }
        throw new Error(result.message);
      }
      throw new Error('Error getting role permissions');
    } catch (error) {
      throw new Error('Error getting role permissions - ' + error);
    }
  }, []);

  const { data, isPending, error, refetch, isRefetching } = useQuery<getRolesPermissionsResponse | undefined, Error>({
    queryKey: [GET_ROLE_PERMISSIONS_QUERY_KEY],
    queryFn: getRolePermissions,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false
  });
  const [user, setUser] = useState<{ id: string; email: string }>({ id: '', email: '' });

  const [currentInstance, setCurrentInstance] = useState<string>();

  const instanceData: InstanceData | undefined = useMemo(() => {
    if (!data || !currentInstance) return;
    return data.instances[currentInstance];
  }, [data, currentInstance]);

  const getDropdownInstanceOptions = useCallback((): SelectProps.Options => {
    if (!data) return [];
    return Object.entries(data.instances).map(([instanceId, instanceData]) => ({
      label: instanceData.instanceAlias,
      value: instanceId
    }));
  }, [data]);

  const getTableInstanceData = useCallback(() => {
    if (!data) return [];
    return Object.values(data.instances);
  }, [data]);

  const refreshData = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const changeInstance = useCallback(
    (instanceId: string) => {
      StorageHelper.setStoredInstanceId(instanceId);
      setCurrentInstance(instanceId);
      const instanceAlias = data?.instances?.[instanceId]?.instanceAlias;
      if (!instanceAlias) return;
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('instance', instanceAlias);
          return newParams;
        },
        { replace: true }
      );
    },
    [data, searchParams, setSearchParams]
  );

  const getInstanceIdFromUrlInstanceAlias = useCallback(() => {
    if (!data) return;
    const instanceAlias = searchParams.get('instance');
    if (!instanceAlias) return;
    const instanceId = Object.entries(data.instances).find(
      ([_, instanceData]) => instanceData.instanceAlias === instanceAlias
    )?.[0];
    return instanceId;
  }, [data, searchParams]);

  useLayoutEffect(() => {
    if (data) {
      if (Object.keys(data.instances).length === 1) {
        const instanceId = Object.keys(data.instances)[0];
        StorageHelper.setStoredInstanceId(instanceId);
        setCurrentInstance(instanceId);
      } else {
        const instanceId = getInstanceIdFromUrlInstanceAlias();
        if (instanceId) {
          StorageHelper.setStoredInstanceId(instanceId);
          setCurrentInstance(instanceId);
        }
      }
    }
  }, [data, getInstanceIdFromUrlInstanceAlias]);

  const getPathWithInstanceAlias = (path: string) => {
    const instancePart =
      instanceData && data?.instances && Object.keys(data.instances).length > 1
        ? `?instance=${instanceData.instanceAlias}`
        : '';
    return `${path}${instancePart}`;
  };

  useEffect(() => {
    if (data?.instances && !searchParams.get('instance')) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        const storedInstanceId = StorageHelper.getStoredInstanceId();
        const targetInstanceId =
          storedInstanceId && data.instances[storedInstanceId] ? storedInstanceId : Object.keys(data.instances)[0];
        if (data.instances && targetInstanceId) {
          newParams.set('instance', data.instances[targetInstanceId].instanceAlias);
        }
        return newParams;
      });
    }
  }, [data?.instances, searchParams]);

  useEffect(() => {
    const getUserId = async () => {
      const result = await fetchUserAttributes();
      setUser({
        id: result['custom:user-id'] || result.sub || '',
        email: result.email ?? ''
      });
    };
    getUserId();
  }, []);

  const value = {
    currentInstance,
    setCurrentInstance: changeInstance,
    instanceData,
    loading: isPending || isRefetching,
    error: error,
    getDropdownInstanceOptions,
    getTableInstanceData,
    refetch: refreshData,
    getPathWithInstanceAlias,
    user
  };

  const content = useMemo(() => {
    // Skip instance validation for access-management paths
    const isAccessManagementPath = pathname.startsWith('/admin/access-management');

    if (!error && !isPending && data && pathname !== '/portal/invalid-instance' && !isAccessManagementPath) {
      const urlInstance = searchParams.get('instance');
      if (urlInstance && !getInstanceIdFromUrlInstanceAlias()) {
        return <Navigate to={'/portal/invalid-instance'} />;
      }
      if (!urlInstance && Object.keys(data.instances).length !== 1 && pathname !== '/portal') {
        return <Navigate to={'/portal/invalid-instance'} />;
      }
    }
    return <Outlet />;
  }, [data, searchParams, getInstanceIdFromUrlInstanceAlias, pathname]);

  return <AuthContext.Provider value={value}>{content}</AuthContext.Provider>;
};

export default AuthProvider;
